import { supabase } from '../supabase/client';
import { logActivity } from './activityService';
import type { SMSLogFilters, SendSMSData } from '@/types/sms';
import { replaceSMSVariables } from '@/types/sms';

/**
 * Get SMS templates
 */
export const getTemplates = async (category?: string) => {
  try {
    let query = supabase
      .from('sms_templates')
      .select('*')
      .eq('is_active', true)
      .order('category')
      .order('name');

    if (category) {
      query = query.eq('category', category);
    }

    const { data, error } = await query;

    if (error) {
      console.error('[SMSService] Get templates error:', error);
      return { success: false, error: error.message };
    }

    return { success: true, data };
  } catch (error: any) {
    console.error('[SMSService] Unexpected error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Create SMS template
 */
export const createTemplate = async (templateData: any, staffUserId: string) => {
  try {
    const { data, error } = await supabase
      .from('sms_templates')
      .insert({
        ...templateData,
        created_by: staffUserId,
      })
      .select()
      .single();

    if (error) {
      console.error('[SMSService] Create template error:', error);
      return { success: false, error: error.message };
    }

    // Log activity
    await logActivity({
      staff_user_id: staffUserId,
      action_type: 'create_sms_template',
      entity_type: 'sms_template',
      entity_id: (data as any).id,
    });

    return { success: true, data };
  } catch (error: any) {
    console.error('[SMSService] Unexpected error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Send SMS via Edge Function
 */
export const sendSMS = async (smsData: SendSMSData, staffUserId: string) => {
  try {
    // Get template
    const { data: template, error: templateError } = await supabase
      .from('sms_templates')
      .select('*')
      .eq('id', smsData.template_id)
      .single() as any;

    if (templateError || !template) {
      return { success: false, error: 'Template not found' };
    }

    // Replace variables in template
    const message = replaceSMSVariables((template as any).message_template, smsData.variable_values);

    // Call Edge Function
    const { data, error } = await supabase.functions.invoke('send-sms', {
      body: {
        phone_number: smsData.phone_number,
        message,
        staff_user_id: staffUserId,
        customer_user_id: smsData.customer_user_id,
        template_id: smsData.template_id,
        case_id: smsData.case_id,
      },
    });

    if (error) {
      console.error('[SMSService] Send SMS error:', error);
      return { success: false, error: error.message };
    }

    // Log activity
    await logActivity({
      staff_user_id: staffUserId,
      action_type: 'send_sms',
      entity_type: 'customer',
      entity_id: smsData.customer_user_id,
      details: { phone: smsData.phone_number, template: template.name },
    });

    return { success: true, data };
  } catch (error: any) {
    console.error('[SMSService] Unexpected error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get SMS sent log
 */
export const getSentLog = async (filters?: SMSLogFilters) => {
  try {
    let query = supabase
      .from('sms_sent_log')
      .select(`
        *,
        staff_user:staff_users!staff_user_id(first_name, last_name, email),
        customer:users!customer_user_id(first_name, last_name, phone_number),
        template:sms_templates(name, category)
      `)
      .order('created_at', { ascending: false })
      .limit(100);

    if (filters?.staff_user_id) {
      query = query.eq('staff_user_id', filters.staff_user_id);
    }

    if (filters?.customer_user_id) {
      query = query.eq('customer_user_id', filters.customer_user_id);
    }

    if (filters?.delivery_status) {
      query = query.eq('delivery_status', filters.delivery_status);
    }

    if (filters?.date_from) {
      query = query.gte('created_at', filters.date_from);
    }

    if (filters?.date_to) {
      query = query.lte('created_at', filters.date_to);
    }

    const { data, error } = await query;

    if (error) {
      console.error('[SMSService] Get sent log error:', error);
      return { success: false, error: error.message };
    }

    return { success: true, data };
  } catch (error: any) {
    console.error('[SMSService] Unexpected error:', error);
    return { success: false, error: error.message };
  }
};

export const smsService = {
  getTemplates,
  createTemplate,
  sendSMS,
  getSentLog,
};

